import * as React from 'react';
import { InputAdornment, Select, SelectChangeEvent, MenuItem, TextField, TextFieldProps } from '@mui/material';

/** OData filter operator suffixes cycled through by the search-type dropdown. */
type MInputFilterSearchType = 'include' | 'exact' | 'startswith';

/**
 * Props for {@link MInputFilter}.
 */
interface MInputFilterProps extends Omit<TextFieldProps, 'onChange' | 'defaultValue' | 'name'> {
    /** Field label. */
    label?: string;
    /** Base OData field name; combined with the operator suffix, e.g. `name_contains`. */
    name: string;
    /** Unused by this control; kept for parity with sibling filter controls. */
    choices?: unknown[];
    /** Unused by this control; kept for parity with sibling filter controls. */
    isEmpty?: boolean;
    /** Initial search text. */
    defaultValue?: string;
    /**
     * Called whenever the search text or operator changes.
     *
     * @param value - The current search text.
     * @param filterName - The OData field name with its operator suffix, e.g. `name_contains`.
     */
    onChange: (value: string, filterName: string) => void;
    /** Pre-selected operator; defaults to `'include'` (the `_contains` operator). */
    searchType?: MInputFilterSearchType;
}

function extractSearchType (searchType?: MInputFilterSearchType): 0 | 1 | 2 | null {
    if (searchType) return searchType === 'include' ? 0 : searchType === 'exact' ? 1 : 2;
    return null;
}

/**
 * A `TextField` with a leading operator dropdown (`INCLUDES` / `EXACT MATCH` /
 * `STARTS WITH`), reporting both the search text and the resulting OData
 * field name (e.g. `name_contains`) via `onChange`.
 */
const MInputFilter: React.FC<MInputFilterProps> = ({ label, name, choices: _choices, isEmpty: _isEmpty, defaultValue, onChange, searchType, ...props }) => {
    const [searchText, setSearchText] = React.useState('');
    const [searchIndex, setSearchIndex] = React.useState<number | null>(null);

    React.useEffect(() => {
        setSearchText(defaultValue ?? '');
    }, [defaultValue]);

    React.useEffect(() => {
        if (searchType) {
            handleSearchType(extractSearchType(searchType));
        }
    }, [searchType]);

    React.useEffect(() => {
        setFilterName(searchIndex);
    }, [searchText, searchIndex]);

    const handleSearchType = (param: SelectChangeEvent<number> | number | null) => {
        const index = param && typeof param === 'object' ? Number(param.target.value) : param;
        setSearchIndex(index);
    };

    const setFilterName = (value: number | null) => {
        let operator: string;
        switch (value) {
            case 1:
                operator = '_eq';
                break;
            case 2:
                operator = '_startswith';
                break;
            default:
                operator = '_contains';
                break;
        }
        onChange(searchText, name + operator);
    };

    const handleTextChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = ev.target;
        setSearchText(value);
    };

    return (
        <TextField
            {...props}
            type='search'
            label={label}
            variant="standard"
            onChange={handleTextChange}
            value={searchText}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Select defaultValue={extractSearchType(searchType) ?? 0}
                                disableUnderline={true}
                                variant="standard"
                                onChange={handleSearchType}
                            >
                                <MenuItem value={0}>INCLUDES</MenuItem>
                                <MenuItem value={1}>EXACT MATCH</MenuItem>
                                <MenuItem value={2}>STARTS WITH</MenuItem>
                            </Select>
                        </InputAdornment>
                    ),
                }
            }}
        />
    );
};

export default React.memo(MInputFilter);
